// src/pages/AboutPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import HeaderBar from '../components/HeaderBar';
import Footer from '../components/Footer';
import '../styles/LandingPage.css';

export default function AboutPage() {
  return (
    <div className="landing-page">
      <HeaderBar />
      <main style={{ maxWidth: 720, margin: '16px auto', width: '100%', padding: '0 16px' }}>
        <div className="card" style={{ textAlign: 'left', margin: '12px auto' }}>
          <h2 style={{ marginTop: 0 }}>
            Anonymous Feedback Surveys, Launched in Seconds
          </h2>

          <p>
            <strong>getSurvey</strong> helps founders, teams, and communities launch anonymous feedback
            surveys quickly, share them anywhere, and turn responses into simple insights.
          </p>

          <p>
            The product is built for moments when you need honest feedback fast: validating a startup idea,
            collecting product feedback, running an anonymous team retro, or prioritizing community requests.
            Create a survey from a prompt, publish it with a public link or access code, and review the response
            patterns without a heavy setup process.
          </p>

          <p>
            The goal is not to replace every form builder. getSurvey is focused on lightweight feedback
            campaigns: ask better questions, remove friction for respondents, and make the results easier
            to understand and share.
          </p>

          <p style={{ background: '#eff6ff', color: '#1e3a8a', padding: '12px 16px', borderRadius: 8 }}>
            <strong>About accounts:</strong> The core experience is accountless by design—no sign-up or sign-in is required
            to create or respond to a survey, making participation effortless. 
            In the future, <em>optional paid features</em> will let users create accounts to manage surveys at scale,
            track actions, and collaborate in teams—all while keeping the base experience frictionless.
          </p>

          <p style={{ background: '#ecfccb', color: '#365314', padding: '12px 16px', borderRadius: 8, marginTop: 8 }}>
            <strong>Open beta:</strong> We’re shipping fast and refining based on real feedback.
            Expect frequent updates as we improve AI summaries, live insights, and “insight → action” workflows.
          </p>

          <h3>Mission</h3>
          <p>
            Our mission is simple: help people collect honest anonymous feedback in minutes, not days.
            getSurvey combines AI-drafted questions, shareable survey links, access-code controls, and
            lightweight insights so creators can learn what people really think.
          </p>

          <h3>What the AI Does for You</h3>
          <ul>
            <li><strong>Drafts surveys from a prompt</strong> or from your notes in seconds.</li>
            <li><strong>Clusters themes and summarizes sentiment</strong> across responses.</li>
            <li><strong>Highlights common priorities</strong> so feedback is easier to scan.</li>
            <li><strong>Supports follow-up surveys</strong> when you need to go deeper.</li>
          </ul>

          <h3>Best Use Cases</h3>
          <ul>
            <li><strong>Startup validation:</strong> test an idea, landing page, or product direction.</li>
            <li><strong>Product feedback:</strong> learn what users want fixed, improved, or built next.</li>
            <li><strong>Anonymous team retros:</strong> collect candid feedback without forcing names into the process.</li>
            <li><strong>Community decisions:</strong> prioritize requests, proposals, events, or shared initiatives.</li>
            <li><strong>Customer insights:</strong> ask simple questions and spot recurring themes quickly.</li>
          </ul>

          <h3>How It Works</h3>
          <ol>
            <li><strong>Create:</strong> type a topic (or paste notes) and let AI propose a survey you can adjust.</li>
            <li><strong>Share:</strong> publish with a public link or protect access with a code.</li>
            <li><strong>Collect:</strong> let respondents answer anonymously with minimal friction.</li>
            <li><strong>Learn:</strong> review responses, discussion, and lightweight insights.</li>
          </ol>

          <h3>Our Approach</h3>
          <ul>
            <li><strong>Accountless by default:</strong> maximize participation by removing sign-up barriers.</li>
            <li><strong>Optional accounts (future, paid):</strong> for organizations needing deeper management, integrations, and analytics.</li>
            <li><strong>Privacy-aware:</strong> designed to value insights over identities.</li>
            <li><strong>Feedback-first:</strong> optimized for quick campaigns, honest responses, and clear next questions.</li>
          </ul>

          <p className="muted" style={{ marginTop: 12 }}>
            Before you launch, review our{' '}
            <Link to="/disclaimer" className="muted" style={{ color: '#2563eb' }}>
              disclaimer &amp; responsible use policy
            </Link>{' '}
            so each survey respects your participants and context.
          </p>

          <div style={{ marginTop: 20 }}>
            <Link
              to="/create-survey"
              className="btn"
              style={{ background: '#2563eb', color: '#ffffff', textDecoration: 'none' }}
            >
              Create a Survey Now
            </Link>
          </div>
        </div>
      </main>
      <Footer betaOnNewLine />
    </div>
  );
}
